import React from 'react'
import {
  bool,
  oneOf,
  string,
  node,
  number,
  exact,
  oneOfType,
  arrayOf,
  shape,
} from 'prop-types'
import * as Styled from './CrossResultList.styles'

// component stack
import { Button, Container, Grid, Tooltip } from 'components/atoms'

// libraries && helpers
import { useSelector } from 'react-redux'
import { useMobileSwitch } from 'hooks'
import { getReplacedString } from 'settings/app/helpers'
import { buildUrl } from 'settings/app/urlHelpers'
import { TextContext } from 'hocs/TextContextProvider/TextContextProvider'
import { mappedEntryPointNamesQSS } from 'settings/app/constants'
import DOMPurify from 'dompurify'

const CrossResultList = (props) => {
  const {
    type,
    open,
    activeFilter,
    filterCollection,
    activeSort,
    children,
    numberOfResults,
    redirectURLs,
    tooltip,
    ...rest
  } = props
  const textContext = React.useContext(TextContext)
  const isMobile = useMobileSwitch()
  const [showResults, setShowResults] = React.useState(open)
  const state = useSelector((state) => state)

  // doctors and asv teams will have the same "route || search"
  const getDeepLink = () => {
    const backLink = `${ redirectURLs[type === 'asv_teams' ? 'doctors' : type] }${
      buildUrl(state, {
        ...state.search.params,
        collection: filterCollection,
      })[1]
    }`

    // qss can only have initial searches (cuz no clickable filter etc.)
    const params = backLink.split('?')
    if (params.length > 1 && params[1] !== '') {
      // return backLink
      return `${ backLink }&initial_search=true`
    }

    return `${ backLink }?initial_search=true`
  }

  const getToolTip = () => (
    <Tooltip
      arrow='left'
      className='tooltip'
      dialogTitle={ tooltip.title }
      infoIconId={ `info-icon` }
      tooltipId={ `tooltip` }>
      <span
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(tooltip.tip) }}
      />
    </Tooltip>
  )

  return (
    <Styled.CrossResultList showResults={ showResults } { ...rest }>
      {!showResults && (
        <Container
          className='container-content closed-result-container'
          isFlat={ true }
          data-cy-category={ mappedEntryPointNamesQSS[type] }
          data-cy='cross-search-container'
          data-cy-open-state='closed'
          data-cy-number-of-results={ numberOfResults }
          onClick={ () => setShowResults(!showResults) }>
          <div className='title-container'>
            <div className='title-closed'>{mappedEntryPointNamesQSS[type]}</div>
            <div className='results-closed'>
              {' '}
              {getReplacedString({
                search: textContext['crossResultContainer.btnShow.resultsTile'],
                replaceWith: numberOfResults,
              })}
              {tooltip && getToolTip()}
            </div>
          </div>
          <a href={ getDeepLink() } onClick={ (e) => e.stopPropagation() }>
            <Button
              id='collapse-results'
              text={ textContext['crossResultContainer.link.showResults'] }
              disabled={ false }
              className='btn-link more-link header-btn'
            />
          </a>
        </Container>
      )}
      {showResults && (
        <Grid
          container
          className='open-result-container'
          data-cy-category={ mappedEntryPointNamesQSS[type] }
          data-cy='cross-search-container'
          data-cy-open-state='open'
          data-cy-number-of-results={ numberOfResults }>
          <Grid item xs={ 12 } sm={ 4 } className='results-information'>
            <div className='title-open'>
              {`${ mappedEntryPointNamesQSS[type] } (${ numberOfResults })`}
              {tooltip && getToolTip()}
            </div>
            {activeFilter && !isMobile && (
              <div className='filters-by'>
                {textContext['crossResultContainer.filters.by']} {activeFilter}.
              </div>
            )}
            <a
              href={ getDeepLink() }
              title={ textContext[`crossResultContainer.btnShow.${ type }`] }
              className='more-link'>
              {textContext[`crossResultContainer.btnShow.${ type }`]}
            </a>
          </Grid>
          <Grid item xs={ 12 } sm={ 8 } className='results-container'>
            {!isMobile && (
              <div className='results-header'>
                <div className='results-header-details'>
                  {activeSort &&
                    `${ textContext['crossResultContainer.sorted.by'] }  ${ activeSort }`}
                </div>
                <a href={ getDeepLink() }>
                  <Button
                    id='show-result-header'
                    text={ textContext['crossResultContainer.btnShow.all'] }
                    disabled={ false }
                    className='btn-link'
                  />
                </a>
              </div>
            )}
            <div
              className={ `${
                numberOfResults >= 4 ? 'with-bottom-course' : ''
              } results` }>
              {children}
              {numberOfResults >= 4 && (
                <a href={ getDeepLink() }>
                  <Button
                    id={ `show-all-results-${ type }` }
                    data-cy={ `show-all-results-${ type }` }
                    text={ getReplacedString({
                      search:
                        textContext['crossResultContainer.btnShow.allButton'],
                      replaceWith: `${ numberOfResults } ${ mappedEntryPointNamesQSS[type] }`,
                    }) }
                  />
                </a>
              )}
            </div>
          </Grid>
        </Grid>
      )}
    </Styled.CrossResultList>
  )
}

CrossResultList.defaultProps = {
  open: false,
  activeFilter: false,
  activeSort: false,
}

CrossResultList.propTypes = {
  type: oneOf([
    'doctors',
    'hospitals',
    'emergencies',
    'midwives',
    'ambulances',
    'asv_teams',
  ]),
  open: bool,
  activeFilter: oneOfType([string, bool]),
  filterCollection: arrayOf(
    shape({
      chip_label: string,
      isDuplicate: string,
      key: string,
      label: string,
      selected: bool,
      tip: string,
      type: oneOf(['boolean']),
    })
  ),
  activeSort: oneOfType([string, bool]),
  children: node,
  numberOfResults: number.isRequired,
  redirectURLs: exact({
    doctors: string,
    hospitals: string,
    midwives: string,
    ambulances: string,
    oncallservices: string,
    cross: string,
    // abs2
    dayandnightcare: string,
    carehomes: string,
    caresearch: string,
    careservices: string,
    caresupport: string,
  }),
  tooltip: oneOfType([
    bool,
    exact({
      title: string,
      tip: string,
    }),
  ]),
}

export default CrossResultList
